import { useId, useMemo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, PaperProps } from '@mui/material';
import Draggable from 'react-draggable';

import { AVADialogProps } from './types';
import './AVADialog.css';

export const AVADialog = ({
    id,
    open,
    title,
    children,
    actions,
    onClose,
    width,
    maxWidth = 'md',
    dataTestId = 'dialog-component',
    draggable = true,
    backdropSx,
    actionsSx,
    disableBackdropClose = false,
}: AVADialogProps) => {
    // useId guarantees uniqueness per instance, preventing duplicate DOM ids when multiple dialogs coexist
    // remove colons from the generated id to ensure valid HTML id attributes
    const titleId = useId().replace(/:/g, '_');

    const DraggablePaper = useMemo(
        () => (props: PaperProps) => (
            <Draggable
                handle={`#${titleId}`}
                cancel={'[class*="MuiDialogContent-root"]'}
            >
                <Paper {...props} />
            </Draggable>
        ),
        [titleId],
    );

    const handleClose = (_: unknown, reason: string) => {
        if (disableBackdropClose && reason === 'backdropClick') return;
        onClose?.();
    };

    return (
        <Dialog
            data-testid={dataTestId || 'muiDialog'}
            id={id}
            className='muiDialog'
            aria-labelledby={titleId}
            disableEnforceFocus={true}
            open={Boolean(open)}
            maxWidth={maxWidth}
            onClose={handleClose}
            slotProps={{
                backdrop: {
                    sx: {
                        ...backdropSx,
                    },
                },
            }}
            PaperProps={{
                sx: {
                    width: width,
                    maxWidth: maxWidth || '100%',
                },
            }}
            fullWidth
            PaperComponent={draggable ? DraggablePaper : undefined}
        >
            <DialogTitle
                className='popup-header-mui xxx'
                id={titleId}
                sx={{
                    color: 'white',
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: '0 0 4px 4px',
                    cursor: draggable ? 'move' : 'default',
                }}
            >
                {title}
                <IconButton
                    onClick={onClose}
                    aria-label='close-avadialog-button'
                >
                    <CloseIcon style={{ color: 'white', fill: 'white' }} />
                </IconButton>
            </DialogTitle>
            <DialogContent>{children}</DialogContent>
            {actions && <DialogActions sx={{ backgroundColor: '#fff', ...actionsSx }}>{actions}</DialogActions>}
        </Dialog>
    );
};
